/**
 * `<WebSocketProvider>` — AppShell-mounted Context provider that owns the
 * **single** WebSocket connection per tab. Every realtime consumer
 * (`useGlobalRealtime`, `useChatGlobalRealtime`, `useChatRealtime`,
 * `useRealtimeBoard`, future presence-aware surfaces) subscribes to
 * frames through `useWebSocketSubscription` instead of opening its own
 * `useWebSocket`. One socket per tab, broadcast surface area shrinks
 * 2-4×, presence math stays clean (connection-count = tabs, not tabs ×
 * hooks), the `Live` pill in the sidebar reflects the actual single
 * socket's state.
 *
 * Token-refresh-aware: the provider holds `useFreshAccessToken`, so when
 * Cognito rotates the access token the `token` prop on the inner
 * `useWebSocket` changes → the hook's connect-effect re-fires (close +
 * reopen with the fresh token). Result: every token rotation produces
 * a clean WS auth state and a clean DDB row, no stale-token-loop class.
 *
 * Subscribers stay subscribed across reconnects. The provider keeps a
 * stable set of `(message) => void` handlers; the underlying socket
 * comes + goes underneath, but the subscriber-side identity is stable
 * so consumers never miss a frame because the socket happened to be
 * mid-reconnect when they mounted.
 *
 * Mounted inside `<AppShell>` so the provider only lives for
 * authenticated routes (no WS attempt on `/login`, `/p/docs/:token`,
 * etc. — those routes bypass AppShell entirely per `__root.tsx`'s
 * `UNAUTHENTICATED_ROUTES` + `UNAUTHENTICATED_ROUTE_PREFIXES`).
 */

import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'

import { useFreshAccessToken } from '@/auth/use-fresh-access-token'

import type { ServerMessage, WebSocketStatus } from './use-websocket'
import { useWebSocket } from './use-websocket'
import { resolveTenantForWs } from './ws-tenant'

const WS_URL = (import.meta.env.VITE_WS_URL as string | undefined) ?? ''

/**
 * Idle-disconnect timeout — close the WS after this many ms of no user
 * activity. Per ADR-033 (matches Slack / Discord / Linear). This is the
 * LONG path: tab visible, no mouse / keyboard / scroll for 10 min →
 * suspend. Tuned high enough to avoid churn during quiet reading
 * sessions where the user is still at their desk.
 */
const IDLE_TIMEOUT_MS = 10 * 60 * 1000

/**
 * Hidden-tab grace timer — close the WS after this many ms of the tab
 * being `document.visibilityState === 'hidden'`. Per the ADR-033
 * amendment 2026-06-01: visibility is the primary "user has stepped
 * away from the page" signal (alt-tab, minimize, different macOS Space,
 * laptop lid close), and a 60s grace covers normal quick alt-tabbing
 * without triggering the suspend. Pre-amendment, hidden tabs sat in a
 * 3m37s reconnect-churn cycle until the long idle timer fired —
 * eating ~4 DB transactions per cycle. The hidden grace cuts that to
 * ~1 transaction per excursion (the initial $connect after the user
 * returns).
 */
const HIDDEN_GRACE_MS = 60 * 1000

/**
 * Activity-event debounce — `mousemove` fires constantly when the user
 * is at their desk; debouncing the timer-reset to once per ~quarter-
 * second keeps the per-event work to a `Date.now()` compare + a noop.
 */
const ACTIVITY_DEBOUNCE_MS = 250

type Subscriber = (message: ServerMessage) => void

interface WebSocketContextValue {
  status: WebSocketStatus
  /**
   * Subscribe a handler. Returns an unsubscribe function. Handlers
   * receive every server frame; consumers filter by `message.type`
   * client-side. Multiple subscribers see the same frames (no fan-in
   * dedup needed — each handler owns its own cache invalidation).
   */
  subscribe: (handler: Subscriber) => () => void
  /**
   * Send a client→server frame over the single shared socket. No-op when
   * the socket isn't `OPEN` (the underlying `useWebSocket.send` swallows
   * it). Used for app-semantic client messages like the chat
   * "viewing this conversation" suppression signal; rides the same
   * channel the heartbeat `{ type: 'ping' }` uses.
   */
  send: (message: object) => void
}

const WebSocketContext = createContext<WebSocketContextValue | null>(null)

export interface WebSocketProviderProps {
  children: ReactNode
}

export function WebSocketProvider({ children }: WebSocketProviderProps) {
  const { token, refresh } = useFreshAccessToken()
  const tenant = resolveTenantForWs()

  // Idle-disconnect (ADR-033). Closes the WS after N min of no user
  // activity and reconnects on the next interaction. The `idle` flag
  // flows into `useWebSocket` via the `suspended` option; the hook
  // owns the actual close/reopen lifecycle.
  const [idle, setIdle] = useState(false)
  // Ref so the activity callback (stable identity, listeners installed
  // once) can check the current idle state without re-installing
  // listeners on every transition.
  const idleRef = useRef(false)
  useEffect(() => {
    idleRef.current = idle
  }, [idle])

  // Activity reset — bumped by every interaction event. Sets a fresh
  // 10-min timeout; if no further event arrives in the window, the
  // timeout fires and `setIdle(true)` flips `suspended` on the hook.
  // Debounced so the high-frequency `mousemove` stream doesn't burn
  // CPU resetting the timer every pixel.
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const lastResetAtRef = useRef(0)
  const markActive = useCallback(() => {
    const now = Date.now()
    if (now - lastResetAtRef.current < ACTIVITY_DEBOUNCE_MS) return
    lastResetAtRef.current = now
    if (idleRef.current) {
      setIdle(false)
    }
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current)
    idleTimerRef.current = setTimeout(() => {
      idleTimerRef.current = null
      setIdle(true)
    }, IDLE_TIMEOUT_MS)
  }, [])

  // Hidden-tab grace timer. Separate from the activity-driven idle
  // timer above: this fires when the page-level visibility flips to
  // 'hidden' (alt-tab, minimize, different macOS Space, lid close).
  // 60s grace so a quick alt-tab-and-back doesn't churn the WS;
  // anything longer suspends until the user returns. See ADR-033
  // amendment 2026-06-01.
  const hiddenTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const clearHiddenTimer = useCallback(() => {
    if (hiddenTimerRef.current) {
      clearTimeout(hiddenTimerRef.current)
      hiddenTimerRef.current = null
    }
  }, [])

  useEffect(() => {
    if (typeof window === 'undefined') return undefined

    // Capture-phase listeners on `window` so a deeply-nested React
    // handler that calls `stopPropagation()` (e.g., the editor's
    // contenteditable) can't suppress activity detection. Passive
    // because we never `preventDefault` on these events; this is
    // strictly observation.
    const ACTIVITY_EVENTS = [
      'mousemove',
      'keydown',
      'scroll',
      'wheel',
      'touchstart',
      'pointerdown',
    ] as const
    const listenerOptions: AddEventListenerOptions = { passive: true, capture: true }
    for (const ev of ACTIVITY_EVENTS) {
      window.addEventListener(ev, markActive, listenerOptions)
    }
    // `focus` doesn't capture-bubble the same way; bind on window.
    window.addEventListener('focus', markActive)
    // Visibility transitions drive the short-grace hidden timer (per
    // ADR-033 amendment). On `hidden`, start the 60s timer; on
    // `visible`, cancel it and treat the return as activity (resets
    // the long idle timer too via `markActive`).
    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        clearHiddenTimer()
        hiddenTimerRef.current = setTimeout(() => {
          hiddenTimerRef.current = null
          setIdle(true)
        }, HIDDEN_GRACE_MS)
      } else if (document.visibilityState === 'visible') {
        clearHiddenTimer()
        markActive()
      }
    }
    document.addEventListener('visibilitychange', onVisibilityChange)

    // Arm the initial timer so a freshly-mounted-then-untouched tab
    // still goes idle after the timeout.
    markActive()
    // Edge case: if the page mounts while already hidden (e.g., a tab
    // restored on browser launch but not focused), kick the hidden
    // grace timer so we don't sit waiting for a visibilitychange that
    // already happened pre-mount.
    if (document.visibilityState === 'hidden') {
      hiddenTimerRef.current = setTimeout(() => {
        hiddenTimerRef.current = null
        setIdle(true)
      }, HIDDEN_GRACE_MS)
    }

    return () => {
      for (const ev of ACTIVITY_EVENTS) {
        window.removeEventListener(ev, markActive, listenerOptions)
      }
      window.removeEventListener('focus', markActive)
      document.removeEventListener('visibilitychange', onVisibilityChange)
      if (idleTimerRef.current) {
        clearTimeout(idleTimerRef.current)
        idleTimerRef.current = null
      }
      clearHiddenTimer()
    }
  }, [markActive, clearHiddenTimer])

  // Subscribers tracked in a ref so adding / removing a handler doesn't
  // re-trigger the underlying `useWebSocket` (which would close + reopen
  // the socket — every mount of a feature surface would briefly break
  // presence). The dispatch function below reads from this ref each
  // frame, so swap-in changes take effect on the very next message
  // without re-subscribing.
  const subscribersRef = useRef<Set<Subscriber>>(new Set())

  const dispatch = useCallback((message: ServerMessage) => {
    // Snapshot subscribers before iterating so a handler that calls
    // subscribe/unsubscribe synchronously (e.g., self-routing logic)
    // doesn't mutate the set mid-loop. Tiny copy; subscriber count is
    // bounded by feature-folder count (~5).
    const snapshot = Array.from(subscribersRef.current)
    for (const sub of snapshot) {
      try {
        sub(message)
      } catch (err) {
        // One bad subscriber must not stop other subscribers from
        // seeing the frame. Log + carry on.
        console.error('[ws] subscriber threw', err)
      }
    }
  }, [])

  const onAuthFailure = useCallback(() => {
    void (async () => {
      await refresh()
    })()
  }, [refresh])

  const enabled = Boolean(WS_URL) && Boolean(token) && Boolean(tenant)

  const { status, send } = useWebSocket({
    url: WS_URL,
    token,
    tenant,
    onMessage: dispatch,
    onAuthFailure,
    enabled,
    suspended: idle,
  })

  const subscribe = useCallback<WebSocketContextValue['subscribe']>((handler) => {
    subscribersRef.current.add(handler)
    return () => {
      subscribersRef.current.delete(handler)
    }
  }, [])

  // Memoize the context value so consumers don't re-render every
  // status tick unless they actually read `status`. The `subscribe`
  // callback is already stable (useCallback with empty deps); `send`
  // is stable too (useCallback with empty deps inside `useWebSocket`).
  const [value, setValue] = useState<WebSocketContextValue>(() => ({
    status,
    subscribe,
    send,
  }))
  // Update only the `status` field when it changes; preserve the
  // stable `subscribe` + `send` references so subscription effects
  // don't re-run.
  useEffect(() => {
    setValue((prev) => (prev.status === status ? prev : { ...prev, status }))
  }, [status])

  return <WebSocketContext.Provider value={value}>{children}</WebSocketContext.Provider>
}

/**
 * Internal — read the WebSocket context. Throws when called outside a
 * `<WebSocketProvider>`. Consumers should use `useWebSocketSubscription`
 * or `useWebSocketStatus` instead of this directly.
 */
export function useWebSocketContext(): WebSocketContextValue {
  const ctx = useContext(WebSocketContext)
  if (!ctx) {
    throw new Error(
      'useWebSocketContext must be used within <WebSocketProvider> (mounted in AppShell).',
    )
  }
  return ctx
}
